import {
  closeWithoutDocumentCancels,
  lineTotalCents,
  parseMoneyToCents,
  settlesAtOnce,
  tt,
  type FilterChannel,
  type MoneyInput,
} from '@agos/shared'

import { lineNoteOf } from '../order-builder/line-note'

/**
 * Splitting a table's bill.
 *
 * Four people at a table, one of them leaving: they pay for what they ate and
 * the rest of the bill stays open. The original order is closed and re-issued
 * as two — a completed one for what was paid and a live one for what is left —
 * and a fiscal receipt is printed for the paid half only.
 *
 * Ported from legacy `pages/restaurant.tsx`. It is the most consequential
 * arithmetic in the module: a quantity that lands on the wrong side of the
 * split either charges somebody twice or lets a table walk out with food it
 * did not pay for. Pure, and tested.
 */

export interface PayableLine {
  /**
   * `order_items.id` - what a ticked line is addressed by, §9.408.
   *
   * On the wire since the beginning: `OrderDtos.ListLine` and `DetailLine` both open with `Integer
   * id`. This interface simply never declared it, so the split was keyed by array position instead
   * and a tick slid onto other food whenever the board's eight-second poll replaced the array.
   */
  id?: number
  productId?: number
  productName?: string
  customName?: string
  quantity?: number
  unitPrice?: MoneyInput
  /**
   * The rate this line was **charged** at, snapshotted when the order was created — §9.5's per-line
   * VAT, on `order_items.vat_rate` and answered by the order detail. Prefer it over the pair below.
   */
  vatRate?: MoneyInput
  vatRateDineIn?: MoneyInput
  vatRateDelivery?: MoneyInput
  /**
   * §10A.110, the owner's Q6 - what the line **is**, carried through a split.
   *
   * These were dropped on both halves: a gift came back as a sale in the margin report and in a
   * customer's favourites, a daily-menu meal lost §10A.88's kitchen box, and a burger box's slots
   * came back loose. The money was never affected - the server conserves prices and re-prices
   * nothing.
   */
  giftRuleId?: number
  giftRuleType?: string
  lunchMealType?: string
  boxGroupId?: string
  boxGroupName?: string
  boxSlotLabel?: string
  /**
   * The line's own note for the kitchen - §9.690, `BACKEND.md` §10A.348. On the board's row since that
   * entry; `null` and absent are both *no note*. Both halves of a split are re-inserted, so a split
   * that dropped it would take the note off the table that is still eating.
   */
  note?: string | null
}

export interface SplitLine {
  productId: number
  productName: string
  quantity: number
  unitPrice: number
  /** §10A.110 - optional on the wire, and absent means what it always meant. */
  giftRuleId?: number
  giftRuleType?: string
  lunchMealType?: string
  boxGroupId?: string
  boxGroupName?: string
  boxSlotLabel?: string
  /** §9.690 - trimmed, and absent when the line has none. */
  note?: string
}

/**
 * The identity fields a split half inherits from the line it came from - §10A.110.
 *
 * Spelled once, because the paid half and the remaining half are built separately and a field added
 * to one and forgotten on the other would split a gift into a gift and a sale.
 */
function identityOf(
  line: PayableLine,
): Omit<SplitLine, 'productId' | 'productName' | 'quantity' | 'unitPrice'> {
  return {
    ...(line.giftRuleId === undefined ? {} : { giftRuleId: line.giftRuleId }),
    ...(line.giftRuleType === undefined ? {} : { giftRuleType: line.giftRuleType }),
    ...(line.lunchMealType === undefined ? {} : { lunchMealType: line.lunchMealType }),
    ...(line.boxGroupId === undefined ? {} : { boxGroupId: line.boxGroupId }),
    ...(line.boxGroupName === undefined ? {} : { boxGroupName: line.boxGroupName }),
    ...(line.boxSlotLabel === undefined ? {} : { boxSlotLabel: line.boxSlotLabel }),
    // §9.690 - the note goes with the food to whichever half it lands on.
    ...(lineNoteOf(line) === undefined ? {} : { note: lineNoteOf(line) }),
  }
}

export interface SplitResult {
  paidItems: SplitLine[]
  remainingItems: SplitLine[]
  paidCents: number
  remainingCents: number
}

/**
 * What a line is called when nothing named it. It reaches a fiscal receipt, so
 * it is not translated — the register prints Slovak whatever the manager's UI
 * language is.
 */
// eslint-disable-next-line no-restricted-syntax -- printed on the receipt, not shown in the UI
const UNNAMED_LINE = 'Položka'

/**
 * A line's name as the split sends it - exported for `sameBill` (§9.769), which has to key a line exactly as
 * `split-payment` is sent it, because the server compares by that name.
 */
export function nameOf(line: PayableLine): string {
  return line.productName ?? line.customName ?? UNNAMED_LINE
}

/**
 * How a ticked line is addressed - §9.408.
 *
 * **`order_items.id`, not the line's position.** This was keyed by position, for a reason the old
 * docblock stated and which was right as far as it went: *"two lines can carry the same product
 * (FB-053, the same reason the cart is addressed by index)"*. A product id cannot tell three beers
 * rung up separately apart, so it was rejected - correctly. What it missed is that the row has its
 * own identity and the server has always sent it: `OrderDtos.ListLine` and `DetailLine` both open
 * with `Integer id`, filled from `item.getId()`.
 *
 * **Position is not stable data here.** `RestaurantPage` polls the order list every eight seconds so
 * a table cancelled in the kitchen is noticed, and the split itself *"closes the original order and
 * re-issues it"* - so the array a tick was made against is routinely replaced while the dialog is
 * open. The restaurant's report of 2026-09-21: *"označila si niečo a potom keď platila, tak to
 * označilo úplne niečo iné"*.
 *
 * **Negative for a line without an id**, so a fallback key can never collide with a real row. Every
 * line of a persisted order has one; this exists so the type need not pretend otherwise.
 */
export function lineKey(line: PayableLine | undefined, index: number): number {
  return line?.id ?? -(index + 1)
}

/**
 * Everything on the table, ticked - the whole bill as a split.
 *
 * Spelled once because it was spelled three times in `PaymentDialog` and keyed by index in all
 * three. A fourth copy, or one of them left behind on a change like §9.408's, divides a bill by a
 * different rule than the screen showed.
 */
export function wholeBill(lines: PayableLine[]): Map<number, number> {
  return new Map(lines.map((line, index) => [lineKey(line, index), line.quantity ?? 0]))
}

/**
 * Divides the bill by how many of each line the leaving guest is paying for.
 *
 * Partial quantities are the point: three beers on the table, one of them being
 * settled. `selectedByLine` is keyed by {@link lineKey} - the row's own id - which is what lets two
 * lines carrying the same product be told apart **and** survive the eight-second refresh the board
 * runs underneath this dialog. §9.408 has the report that found the difference.
 *
 * A selection larger than the line is clamped, and a negative one is ignored —
 * neither should reach here, and both would otherwise create a remainder that
 * charges the table for food it already paid for.
 */
export function splitSelection(
  lines: PayableLine[],
  selectedByLine: Map<number, number>,
): SplitResult {
  const paidItems: SplitLine[] = []
  const remainingItems: SplitLine[] = []
  let paidCents = 0
  let remainingCents = 0

  lines.forEach((line, index) => {
    const total = line.quantity ?? 0
    const selected = Math.max(0, Math.min(total, selectedByLine.get(lineKey(line, index)) ?? 0))
    const remaining = total - selected
    const unitPrice = parseMoneyToCents(line.unitPrice)

    if (selected > 0) {
      paidItems.push({
        productId: line.productId ?? 0,
        productName: nameOf(line),
        quantity: selected,
        unitPrice: unitPrice / 100,
        ...identityOf(line),
      })
      paidCents += lineTotalCents(line.unitPrice, selected)
    }
    if (remaining > 0) {
      remainingItems.push({
        productId: line.productId ?? 0,
        productName: nameOf(line),
        quantity: remaining,
        unitPrice: unitPrice / 100,
        ...identityOf(line),
      })
      remainingCents += lineTotalCents(line.unitPrice, remaining)
    }
  })

  return { paidItems, remainingItems, paidCents, remainingCents }
}

/** Nothing selected means nothing to split; the caller must not send it. */
export function hasSomethingToPay(selectedByIndex: Map<number, number>): boolean {
  return [...selectedByIndex.values()].some((quantity) => quantity > 0)
}

/**
 * The VAT rate a line is charged at.
 *
 * Slovakia taxes food eaten in at a different rate from food taken away, and
 * every product carries both. Reading the wrong one puts the wrong tax on a
 * fiscal receipt, which is the kind of error that is discovered at an audit.
 *
 * **The line's own rate wins — FB-192.** `vatRateDineIn`/`vatRateDelivery` are the product's rate
 * *now*; `vatRate` is the rate this line was registered at, snapshotted at creation. They differ the
 * moment somebody edits a product's VAT category, and any order already placed and unpaid then
 * printed the new rate on a tax document. The backend's reprint path documents this hazard and
 * avoids it; this path had it, because the snapshot only reached the order detail on 2026-08-04.
 *
 * The pair is kept as the fallback rather than removed: a line written before the column existed
 * carries no snapshot, and a receipt with no rate at all is worse than one with a stale rate.
 *
 * **That fallback was recorded here as unreachable and is not - §9.390, FB-695.** It said `vat_rate`
 * is set on 32 403 of 32 403 lines, which was true when it was written: two writers of `order_items`
 * stored no rate at all, so an edited or split order now has lines that reach the branch below.
 *
 * **And the branch read the three names, which is not the rule - §10A.164's own defect.** The
 * server's is the channel's `vat_slot`, then its `on_premise`, and the names only for a channel with
 * no row; `bar` was missing from the pair here, and so was every channel a franchise creates - each
 * of them taxed as *taken away* on food handed over on the premises. The channel is optional so a
 * caller that has not loaded one behaves exactly as before, which is what `paymentNeedsWaiter` does.
 */
export function vatRateFor(
  line: PayableLine,
  orderType: string | undefined,
  channel?: { vatSlot?: string | null; onPremise?: boolean },
): MoneyInput {
  if (line.vatRate !== null && line.vatRate !== undefined) return line.vatRate
  return eatenInOn(orderType, channel) ? line.vatRateDineIn : line.vatRateDelivery
}

/**
 * Whether the food is taxed as eaten in - the server's `VatSlots.isEatenIn`, in the same order.
 *
 * `vat_slot` answers it outright where a franchise has set one, because *who owns the order* and
 * *how the food is taxed* are two questions and *Do ruky* is where they differ (§10A.113). Without
 * a slot the channel's `on_premise` answers, and only a channel that does not exist falls back to
 * the three names this used to read on its own.
 */
function eatenInOn(
  orderType: string | undefined,
  channel?: { vatSlot?: string | null; onPremise?: boolean },
): boolean {
  if (channel !== undefined) {
    if (channel.vatSlot === 'dine_in') return true
    if (channel.vatSlot === 'delivery') return false
    if (channel.onPremise !== undefined) return channel.onPremise
  }
  return orderType === 'restaurant' || orderType === 'bar' || orderType === 'dine_in'
}

export interface ReceiptLine {
  name: string
  quantity: number
  unitPrice: number
  vatRate?: number
}

/** The lines as the register wants them, with the rate that applies. */
export function receiptLines(
  lines: PayableLine[],
  orderType: string | undefined,
  channel?: { vatSlot?: string | null; onPremise?: boolean },
): ReceiptLine[] {
  return lines.map((line) => {
    const vatRate = vatRateFor(line, orderType, channel)
    return {
      name: nameOf(line),
      quantity: line.quantity ?? 0,
      unitPrice: parseMoneyToCents(line.unitPrice) / 100,
      ...(vatRate !== null && vatRate !== undefined
        ? { vatRate: parseMoneyToCents(vatRate) / 100 }
        : {}),
    }
  })
}

/**
 * Warnings the register raises that a waiter must not be interrupted by.
 *
 * **Moved to `@agos/shared` by §9.500 and re-exported here.** The dispatch board needs the identical
 * rule - it was discarding every warning the register raised during a batch print - and §5 forbids
 * `manager-delivery` importing this module. Re-exported rather than relocated outright so
 * `PaymentDialog`'s import and this file's nine existing assertions on it are untouched: a moved
 * function whose tests were rewritten beside it proves nothing about the move.
 *
 * The reasoning it was written with is now that function's javadoc, unchanged.
 */
export { isTransientKasaWarning } from '@agos/shared'

/**
 * What the operator is asked before a table is closed - §9.366, FB-654.
 *
 * **The owner's report from a live restaurant:** *"platba kartou, it was automatically closed, and no
 * one used karta. When used hotovosť, bloček also did not printed and it was closed."* The pay button
 * ran straight off the click, and `payAll` closes the table **before** the terminal or the printer is
 * asked - so the close happened either way and the failure arrived afterwards as a toast.
 *
 * **The consequence named is specific to the method, because that is the half that was missing.**
 * *Are you sure* on its own would not have told anybody that the card had not been charged yet.
 */
export interface CloseTableConfirmation {
  title: string
  description: string
  confirmLabel: string
}

/**
 * The question after the payment - §9.367, FB-655.
 *
 * **Legacy asks it there and AGOS asked it before.** `restaurant.tsx:3370` shows *"Prebehla platba
 * úspešne?"* once the document is out, with *"Áno, zatvoriť stôl"* and *"Nie, zopakovať platbu"* -
 * so paying and closing are two decisions, and the owner's *"pay -> waiting for food"* is simply
 * answering the second one later.
 */
export interface CloseTableQuestion {
  title: string
  description: string
  closeLabel: string
  keepOpenLabel: string
}

export function closeTableQuestion(
  tableLabel: string,
  amount: string,
  options: { printed?: boolean } = {},
): CloseTableQuestion {
  return {
    title: tt('Prebehla platba úspešne?'),
    description:
      options.printed === false
        ? // The document did not print, so the close is offered with that said out loud rather than
          // hidden - somebody may still be closing a table that was paid in cash.
          `${tableLabel} · ${amount} · ${tt('Doklad sa nevytlačil.')}`
        : `${tableLabel} · ${amount}`,
    closeLabel: tt('Áno, zatvoriť stôl'),
    // **Not *Zrušiť*.** The owner pressed cancel expecting the table to stay open, which is what it
    // does - so the button says that instead of naming an abandonment.
    keepOpenLabel: tt('Nie, nechať stôl otvorený'),
  }
}

export function closeTableConfirmation(
  paymentMethod: string,
  amount: string,
  options: { partial?: boolean } = {},
): CloseTableConfirmation {
  const what = options.partial === true ? tt('Zaplatiť vybrané') : tt('Uzavrieť stôl')
  const consequence =
    paymentMethod === 'card_terminal'
      ? tt('Suma sa načíta na platobnom terminále. Potvrďte až keď je terminál pripravený.')
      : paymentMethod === 'cash'
        ? tt('Vytlačí sa fiškálny doklad. Ak sa nevytlačí, dozviete sa to hneď po potvrdení.')
        : tt('Platba sa zaznamená a doklad sa vytlačí.')
  return {
    title: options.partial === true ? `${what}?` : tt('Naozaj uzavrieť stôl?'),
    // The amount first: it is the one thing somebody checks before pressing a green button again.
    description: `${amount} · ${consequence}`,
    confirmLabel: what,
  }
}

/**
 * Whether this order has already been paid at the till - §9.369, FB-657.
 *
 * **`stampOrder` records it**: a printed receipt sets `fiskal_printed_at` and
 * `fiskal_receipt_number` on the order, so *paid* is a fact the screen can read.
 *
 * **And `fiskalStornoAt` is what makes it unpaid again.** A voided document means the money went
 * back, so the close-only shortcut must not be offered - reading `fiskalPrintedAt` alone would
 * offer to close a table whose payment had been reversed.
 */
export function alreadyPaid(order: { fiskalPrintedAt?: string; fiskalStornoAt?: string }): boolean {
  const printed = (order.fiskalPrintedAt ?? '').trim() !== ''
  const stornoed = (order.fiskalStornoAt ?? '').trim() !== ''
  return printed && !stornoed
}

/**
 * Whether this table can be closed without a fiscal document - §9.447, FB-762.
 *
 * **The exact inverse of `alreadyPaid`, and that is the point rather than a coincidence.** A table
 * with a valid document is closed by the button above it; a table without one had no way to be
 * closed except by attempting a print and answering the failure question. The two gates are
 * mutually exclusive by construction, which `close-without-document.test.ts` asserts over every
 * combination rather than trusting the `!`.
 *
 * **A reversed document counts as no document**, because `alreadyPaid` says so: the money went back,
 * so the table needs closing without a new one.
 *
 * **A cancelled order is refused.** There is nothing to close and the existing *Zatvoriť stôl*
 * already refuses one, so this matches it rather than inventing a second rule.
 */
export function mayCloseWithoutDocument(order: {
  fiskalPrintedAt?: string
  fiskalStornoAt?: string
  status?: string
}): boolean {
  if (order.status === 'cancelled') return false
  return !alreadyPaid(order)
}

/**
 * What the step-up prompt says before closing a table with no document - §9.447.
 *
 * **It names the consequence rather than asking for confirmation in the abstract.** The operator is
 * not being asked *are you sure*; they are being told that no fiscal document will exist for this
 * sale, which is the fact somebody may have to answer for later.
 */
export function closeWithoutDocumentTitle(tableLabel: string, amount: string): string {
  return `${tt('Zatvoriť bez fiškálneho dokladu')} · ${tableLabel} · ${amount}`
}

/**
 * What *Zatvoriť bez fiškálneho dokladu* will do to this order - §9.726, owner O96, `BACKEND.md` §10A.374.
 *
 * - **`cancels`**: `closeWithoutDocumentCancels`, the one shared copy of the server's rule - a channel that settles
 *   at once, with its `unprintedCloseCancels` on (§9.457, the default). Šurany: a counter sale with no printout
 *   did not happen.
 * - **`settles-sale`**: such a channel with the setting off - Sereď's *Do ruky*, where the money was taken at the
 *   counter and nothing was printed, so the sale closes as paid. *"chce zatvoriť bez fiškálneho dokladu nahadzuje
 *   mu storno"* is the report this answers.
 * - **`settles-table`**: every other channel, whose close without a document has always settled - today's wording.
 *
 * **The last two differ only in what is said**, and saying it matters: a counter sale has no table, so *Zatvoriť
 * stôl* over one would be a small false claim on the button that commits the close. Whether it cancels is the
 * shared rule's alone, so the note, the question, its button and its colour cannot disagree. What actually happened
 * is still the server's to say, in `closedOutcome`.
 */
export type UnprintedClose = 'cancels' | 'settles-sale' | 'settles-table'

export function unprintedCloseOf(slug: string, channels: readonly FilterChannel[]): UnprintedClose {
  if (closeWithoutDocumentCancels(slug, channels)) return 'cancels'
  return settlesAtOnce(slug, channels) ? 'settles-sale' : 'settles-table'
}

/**
 * The note under the button, so the consequence is readable before the question opens.
 *
 * **§9.687, owner #116 - it no longer says *Vyžaduje overenie*, because nothing is verified now.** What it
 * says instead is the half of #60 that #116 kept: the close is recorded under the signed-in name, so the
 * operator still knows it is answerable without being asked for a password.
 *
 * **§9.726 - one sentence per {@link UnprintedClose}, in a `switch` with no `default`**, so a fourth answer is
 * TS2366 rather than a close that borrows another's sentence (§9.566's reasoning). The `settles-sale` sentence
 * says *paid, only without a document* and never *zruší*: a note naming the wrong consequence is worse than one
 * naming none (§9.447).
 */
export function closeWithoutDocumentNote(close: UnprintedClose = 'settles-table'): string {
  switch (close) {
    case 'cancels':
      // §9.457 - a settling channel is cancelled rather than closed, so the note has to say which.
      return tt('Bez dokladu sa objednávka zruší - nezostane v tržbe dňa. Zapíše sa pod vaším menom.')
    case 'settles-sale':
      return tt(
        'Objednávka sa uzavrie ako zaplatená, len bez fiškálneho dokladu. Zapíše sa pod vaším menom.',
      )
    case 'settles-table':
      return tt('Pre túto objednávku nevznikne fiškálny doklad. Zapíše sa pod vaším menom.')
  }
}

/**
 * What `POST /orders/{id}/close-without-document` is sent - §9.687, `BACKEND.md` §10A.346.
 *
 * **No `email` and no `password`, by construction.** Owner #116 dropped the login; the server records the
 * signed-in employee and ignores a pair an old client still sends, so this never builds one.
 *
 * **The reason only when one was typed.** An absent key is *no reason*, which the server stores as NULL -
 * a blank string would be the same fact in a second spelling.
 *
 * **The zľava comes in as `closeDiscountPayload`'s answer, unrenamed**, so the close without a document and
 * the printed close compute it with one function - §10A.216 is the entry where two opinions about a zľava on
 * this dialog cost the owner real money.
 */
export interface CloseWithoutDocumentBody {
  paymentMethod: string
  reason?: string
  employeeId?: number
  discountType?: string
  discountAmount?: number
}

export function closeWithoutDocumentBody(input: {
  reason: string
  paymentMethod: string
  employeeId: number | null
  discount: { discountType?: string; discountAmount?: number }
}): CloseWithoutDocumentBody {
  const reason = input.reason.trim()
  return {
    paymentMethod: input.paymentMethod,
    ...(reason === '' ? {} : { reason }),
    ...(input.employeeId === null ? {} : { employeeId: input.employeeId }),
    ...(input.discount.discountType === undefined
      ? {}
      : { discountType: input.discount.discountType, discountAmount: input.discount.discountAmount }),
  }
}

/**
 * `unprintedCloseStatus` lived here until §9.468 and the rule is now the server's.
 *
 * It answered *cancelled* for a channel that settles immediately and *delivered* otherwise - §9.457's
 * rule - and this dialog used it to pick which close to perform. `BACKEND.md` §10A.229 moved the close
 * itself to one endpoint, so the decision moved with it to `CloseWithoutDocumentService`, which owns
 * `sales_channels` and is tested on the capability rather than the slug.
 *
 * **Removed rather than kept**, because its only remaining caller was its own test: a second spelling
 * of a money rule is `agos-one-guard-spelled-twice`, and a function whose only caller is a test is not
 * a function. What the operator is *told* still comes from `closeWithoutDocumentNote` below, and what
 * happened comes back as `closedOutcome`.
 */

/**
 * What the server says it did - §9.468, `BACKEND.md` §10A.229.
 *
 * **The partner to `unprintedCloseStatus` and not its replacement.** That one is what the *note under
 * the button* is written from: the sentence telling the operator, before they press anything, that a
 * *Do ruky* sale will be cancelled rather than closed. This one reads the outcome off the response,
 * because once the close is the server's the server is the one that knows - it owns `sales_channels`,
 * and §9.393 records what reading a channel off a slug costs.
 *
 * Answers an empty string for anything it cannot read, so a caller comparing against `'cancelled'`
 * gets the settled branch rather than a thrown error on an unexpected shape.
 */
export function closedOutcome(response: unknown): string {
  if (typeof response !== 'object' || response === null) return ''
  const outcome = (response as { outcome?: unknown }).outcome
  return typeof outcome === 'string' ? outcome : ''
}

/**
 * Who the server recorded as closing it - §9.687, `BACKEND.md` §10A.346.
 *
 * The endpoint still answers the name under `confirmedByName`, the field the two status writes use, and
 * since owner #116 it is the signed-in employee rather than a second, verified one. An empty string for
 * anything unreadable, so the toast drops the clause rather than printing *Zatvoril: undefined*.
 */
export function closedByName(response: unknown): string {
  if (typeof response !== 'object' || response === null) return ''
  const name = (response as { confirmedByName?: unknown }).confirmedByName
  return typeof name === 'string' ? name.trim() : ''
}

/**
 * What to say over a table that is paid and still open.
 *
 * The receipt number is the point: it is the fact that tells *paid* from *somebody thinks it was
 * paid*, and it is the number they would quote if it had to be stornoed.
 */
export function alreadyPaidNote(order: { fiskalReceiptNumber?: string }): string {
  const number = (order.fiskalReceiptNumber ?? '').trim()
  return number === ''
    ? tt('Doklad pre tento stôl už bol vytlačený. Stôl zostal otvorený.')
    : `${tt('Doklad')} č. ${number} ${tt('už bol vytlačený. Stôl zostal otvorený.')}`
}
